import React, { useState } from "react";
import { colors, displayName } from "../utils/theme.js";
import { inputStyle, primaryBtn, ghostBtn, STATUSES } from "./ui.js";

import { assigneeToValue, valueToAssignee } from "./InlineTaskFields.jsx";

// Right-side drawer to view/edit one task: name, status, assignee, dates, instructions, and
// the append-only status history. onSave(fields) persists (may return false to stay open).
export default function TaskDetail({ task, users, clientId, clientName, onSave, onClose, onDelete }) {
  const [form, setForm] = useState({
    name: task.name || "", status: task.status || "Not Started",
    startDate: task.startDate || "", dueDate: task.dueDate || "",
    instructions: task.instructions || "", assigneeVal: assigneeToValue(task.assignee),
    private: task.private === true,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const uName = (id) => { const x = (users || []).find((y) => y.id === id || y.email === id); return x ? displayName(x) : id; };

  const save = async () => {
    setBusy(true);
    const r = await onSave({
      name: form.name.trim() || task.name, status: form.status,
      startDate: form.startDate || null, dueDate: form.dueDate || null,
      instructions: form.instructions, assignee: valueToAssignee(form.assigneeVal, clientId),
      // Privacy only means something for client work; send it only when changed here, so a
      // drawer opened before someone else marked the task private can't silently un-private it.
      ...(clientId && form.private !== (task.private === true) ? { private: form.private } : {}),
    });
    setBusy(false);
    if (r !== false) onClose();
  };

  const history = Array.isArray(task.history) ? task.history : [];
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1100, display: "flex", justifyContent: "flex-end" }} onClick={onClose}>
      <div style={{ width: 460, maxWidth: "92vw", height: "100%", background: colors.surface, borderLeft: `1px solid ${colors.border}`, padding: 24, overflow: "auto", boxShadow: "-16px 0 40px rgba(0,0,0,0.5)" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: colors.text }}>Task</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", color: colors.textDim, fontSize: 22, cursor: "pointer" }} aria-label="Close">×</button>
        </div>

        <L>Name</L>
        <input style={inputStyle} value={form.name} onChange={(e) => set("name", e.target.value)} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
          <div><L>Status</L>
            <select style={inputStyle} value={form.status} onChange={(e) => set("status", e.target.value)}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div><L>Assignee</L>
            <select style={inputStyle} value={form.assigneeVal} onChange={(e) => set("assigneeVal", e.target.value)}>
              <option value="unassigned">Unassigned</option>
              {clientId && <option value="client">Client</option>}
              {(users || []).map((x) => <option key={x.id} value={x.id}>{displayName(x)}</option>)}
            </select>
          </div>
          <div><L>Start</L><input type="date" style={inputStyle} value={form.startDate} onChange={(e) => set("startDate", e.target.value)} /></div>
          <div><L>Due</L><input type="date" style={inputStyle} value={form.dueDate} onChange={(e) => set("dueDate", e.target.value)} /></div>
        </div>
        <div style={{ marginTop: 12 }}><L>Instructions</L>
          <textarea style={{ ...inputStyle, minHeight: 84, resize: "vertical" }} value={form.instructions} onChange={(e) => set("instructions", e.target.value)} placeholder="How to complete this task…" />
        </div>
        {clientId && (
          <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, fontSize: 13, color: colors.textMuted, cursor: "pointer", userSelect: "none" }}>
            <input type="checkbox" checked={form.private} onChange={(e) => set("private", e.target.checked)} />
            Private — hide from client updates
          </label>
        )}
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button disabled={busy} onClick={save} style={primaryBtn}>{busy ? "Saving…" : "Save task"}</button>
          <button onClick={onClose} style={ghostBtn}>Cancel</button>
          {onDelete && <button onClick={onDelete} style={{ ...ghostBtn, marginLeft: "auto", color: colors.danger, borderColor: "rgba(239,68,68,0.4)" }}>Delete task</button>}
        </div>

        {history.length > 0 && (
          <div style={{ marginTop: 22 }}>
            <L>Status history</L>
            <div style={{ borderLeft: `2px solid ${colors.border}`, paddingLeft: 12, marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
              {[...history].reverse().map((h, i) => (
                <div key={i} style={{ fontSize: 12 }}>
                  <span style={{ color: colors.text }}>{h.status}</span>
                  <span style={{ color: colors.textDim }}> · {new Date(h.at).toLocaleDateString()}{h.by ? ` · ${uName(h.by)}` : ""}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
function L({ children }) {
  return <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, color: colors.textDim, margin: "0 0 4px" }}>{children}</div>;
}
